/*
 * _customled-responsive-tabs.js  (Maranello M1.10.x — Path B rewrite,
 * mobile-accordion variant added M1.10.x continuation)
 *
 * Builds a FULLY OWNED PDP tab system from the content vendor renders
 * in <tabs-navigation>/<tabs-component>:
 *
 *   - Desktop (≥750px): horizontal tab pill row + content panels
 *     (Custom LED's brand-red inactive pills, light-blue active +
 *     panel bg, continuous outline). Implemented as our own
 *     .customled-pdp-tabs structure with click + keyboard handlers.
 *
 *   - Mobile (<750px): a stack of Maranello's NATIVE
 *     <collapsible-expandable> components — same markup the stock
 *     `sections/expandable.liquid` uses for FAQ-style accordions.
 *     Each section toggles independently (multi-open), inheriting
 *     vendor's facet-toggle styling + animation from
 *     component-facets.css.
 *
 * Why both: tabs are space-efficient on wide screens; accordion is
 * scan-friendly on narrow screens (all section names visible at once,
 * no horizontal scrolling, no "where did the rest of the tabs go"
 * discoverability problem). Multi-open behavior on mobile naturally
 * solves the layout-shift problem of single-open accordions
 * (collapsing one section can yank another off-screen on tap).
 *
 * Lifecycle:
 *   1. extractFromVendor() — read vendor's tab nav + content, split
 *      the Description's .responsive-tabs into multiple tabSpecs,
 *      hoist any pre-.responsive-tabs content as a "lead" block.
 *   2. buildAndInsert() — build EITHER the desktop tabs OR the
 *      mobile accordion based on current viewport; insert above
 *      the vendor placeholder.
 *   3. Remove the vendor tabsNav and tabsComp (one-shot — their
 *      content is now in tabSpecs[i].contentNodes references).
 *   4. matchMedia listener — on breakpoint flip, reclaim the
 *      content nodes from the current structure and rebuild in
 *      the new structure. Move-not-clone preserves iframes,
 *      lazy-loaded images, video embeds, etc.
 *
 * Upgrade-proof:
 *   - All custom code lives in this file + _customled-responsive-
 *     tabs.css. No vendor file edits.
 *   - The mobile accordion uses vendor's own <collapsible-expandable>
 *     web component (scripts.js:547) and component-facets.css. If
 *     Truly Fine Pixels renames the component or its classes, this
 *     file is the one place to update.
 *   - Detection selectors (tabs-navigation / tabs-component /
 *     .responsive-tabs / data-handle) are Maranello conventions
 *     unlikely to change.
 *
 * Page-gated to product templates via _customled-stylesheets.liquid.
 */
(function () {
  "use strict";

  // Mobile breakpoint — must match the @media (max-width: 749px) used
  // in our CSS for any mode-specific styling.
  var MOBILE_QUERY = "(max-width: 749px)";

  // Desktop tab skin selector.
  //   true  → Maranello vendor look: plain text labels with animated
  //           underline + chevron tail decoration on active, no pill
  //           background, no panel border. Matches the rest of
  //           Maranello's native sectioned content styling.
  //   false → Custom LED brand pill look: red inactive pills with
  //           white text, blue active tab+panel with continuous
  //           outline. The original custom treatment we iterated on
  //           earlier.
  // Flip this single constant to switch back and forth. Mobile
  // accordion is unaffected — that mode uses vendor's
  // <collapsible-expandable> regardless.
  var USE_VENDOR_SKIN = true;

  // Module-level state so the breakpoint listener can rebuild without
  // walking the DOM again to re-extract from vendor.
  var state = {
    tabSpecs: null,    // Array<{ key, label, contentNodes }>
    leadHtml: "",
    anchor: null,
    currentWrap: null,
    currentMode: null, // "desktop" | "mobile"
  };

  function modeForViewport() {
    if (!window.matchMedia) return "desktop";
    return window.matchMedia(MOBILE_QUERY).matches ? "mobile" : "desktop";
  }

  function init() {
    var tabsNav = document.querySelector(
      "tabs-navigation.product-tab-navigation"
    );
    var tabsComp = document.querySelector(
      "tabs-component.product-tabs-component"
    );
    if (!tabsNav || !tabsComp) return;
    if (tabsNav.dataset.customledHoisted === "1") return;
    tabsNav.dataset.customledHoisted = "1";

    extractFromVendor(tabsNav, tabsComp);
    if (!state.tabSpecs || state.tabSpecs.length === 0) return;

    state.anchor =
      tabsNav.closest(".product-info-description-content") ||
      tabsNav.parentNode;

    buildAndInsert(modeForViewport());

    // Vendor placeholders done — their content now lives in our
    // tabSpecs[i].contentNodes references (already moved into our
    // built wrap by buildAndInsert).
    tabsNav.remove();
    tabsComp.remove();

    bindBreakpointListener();
  }

  // ---- Extract vendor content ------------------------------------

  function extractFromVendor(tabsNav, tabsComp) {
    var tabSpecs = [];
    var leadHtml = "";

    var navItems = tabsNav.querySelectorAll(".tab-nav-item");
    Array.prototype.forEach.call(navItems, function (navItem) {
      var handle = navItem.dataset.handle;
      if (!handle) return;
      var contentEl = tabsComp.querySelector(
        '[data-handle="' + cssEscape(handle) + '"]'
      );
      if (!contentEl) return;

      // Special-case: the Description content holds a .responsive-
      // tabs structure (multiple H2 + DIV pairs) → split each H2/DIV
      // pair into its own tab.
      var rt = contentEl.querySelector(".responsive-tabs");
      if (rt) {
        // Hoist anything BEFORE .responsive-tabs (fitment bullets,
        // intro paragraphs) into a separate lead block above the
        // tabs/accordion.
        var leadClone = contentEl.cloneNode(true);
        var rtInClone = leadClone.querySelector(
          ".responsive-tabs-wrapper, .responsive-tabs"
        );
        if (rtInClone) rtInClone.remove();
        if (leadClone.textContent.trim().length > 0) {
          leadHtml = leadClone.innerHTML;
        }

        var headings = Array.prototype.slice.call(
          rt.querySelectorAll(":scope > h2")
        );
        headings.forEach(function (h) {
          var panel = h.nextElementSibling;
          if (!panel) return;
          tabSpecs.push({
            key: "tab-" + (tabSpecs.length + 1),
            label: h.textContent.trim(),
            // Snapshot children NOW (as Array) so we own a stable
            // reference list. Without snapshotting, if anything else
            // mutates the live NodeList during build, references go
            // stale.
            contentNodes: Array.prototype.slice.call(panel.childNodes),
          });
        });
      } else {
        // Plain tab: take whatever content is inside the tab-content
        // div; label from the vendor nav item's text.
        tabSpecs.push({
          key: "tab-" + (tabSpecs.length + 1),
          label: navItem.textContent.trim(),
          contentNodes: Array.prototype.slice.call(contentEl.childNodes),
        });
      }
    });

    state.tabSpecs = tabSpecs;
    state.leadHtml = leadHtml;
  }

  // ---- Build (mode-dispatched) -----------------------------------

  function buildAndInsert(mode) {
    var wrap = mode === "mobile" ? buildAccordion() : buildTabs();
    state.anchor.insertBefore(wrap, state.anchor.firstChild);
    state.currentWrap = wrap;
    state.currentMode = mode;
  }

  function rebuild(newMode) {
    // Pull content nodes back out of the current structure so they
    // can be moved (not cloned) into the new structure. Cloning
    // would break iframes/video/lazy-loaded images and double-fire
    // any MutationObservers watching descendant content.
    reclaimContentNodes();
    if (state.currentWrap) state.currentWrap.remove();
    buildAndInsert(newMode);
  }

  function reclaimContentNodes() {
    if (!state.currentWrap) return;
    state.tabSpecs.forEach(function (spec) {
      // Both build paths label the content slot with the same
      // data-customled-slot-key attribute so reclaim is uniform.
      var slot = state.currentWrap.querySelector(
        '[data-customled-slot-key="' + cssEscape(spec.key) + '"]'
      );
      if (!slot) return;
      spec.contentNodes = Array.prototype.slice.call(slot.childNodes);
    });
  }

  function bindBreakpointListener() {
    if (!window.matchMedia) return;
    var mql = window.matchMedia(MOBILE_QUERY);
    var handler = function () {
      var newMode = modeForViewport();
      if (newMode !== state.currentMode) {
        rebuild(newMode);
      }
    };
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
    } else if (mql.addListener) {
      mql.addListener(handler); // Safari < 14
    }
  }

  // ---- Desktop tabs build ----------------------------------------

  function buildTabs() {
    var wrap = document.createElement("div");
    wrap.className = "customled-pdp-tabs";
    if (USE_VENDOR_SKIN) {
      wrap.classList.add("customled-pdp-tabs--vendor-skin");
    }

    appendLead(wrap);

    var nav = document.createElement("div");
    nav.className = "customled-pdp-tabs__nav";
    nav.setAttribute("role", "tablist");

    var content = document.createElement("div");
    content.className = "customled-pdp-tabs__content";

    state.tabSpecs.forEach(function (spec, i) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className =
        "customled-pdp-tabs__tab" + (i === 0 ? " is-active" : "");
      btn.setAttribute("role", "tab");
      btn.setAttribute("aria-selected", i === 0 ? "true" : "false");
      btn.setAttribute("tabindex", i === 0 ? "0" : "-1");
      btn.dataset.tabKey = spec.key;
      btn.textContent = spec.label;
      nav.appendChild(btn);

      var panel = document.createElement("div");
      panel.className =
        "customled-pdp-tabs__panel" + (i === 0 ? " is-active" : "");
      panel.setAttribute("role", "tabpanel");
      panel.dataset.tabKey = spec.key;
      // Uniform slot-key attribute for reclaim across modes.
      panel.dataset.customledSlotKey = spec.key;
      panel.hidden = i !== 0;
      moveNodesInto(spec.contentNodes, panel);
      content.appendChild(panel);
    });

    wrap.appendChild(nav);
    wrap.appendChild(content);
    bindTabNav(nav, content);
    // Hydrate any lazy embed already in the initially-active panel (the de-loaded
    // YouTube iframe lives in a later tab, so this typically loads nothing on init).
    hydrateEmbeds(content.querySelector(".customled-pdp-tabs__panel.is-active"));
    return wrap;
  }

  function bindTabNav(nav, content) {
    nav.addEventListener("click", function (e) {
      var btn = e.target.closest("button.customled-pdp-tabs__tab");
      if (!btn) return;
      activateTab(nav, content, btn);
    });

    nav.addEventListener("keydown", function (e) {
      if (
        e.key !== "ArrowLeft" &&
        e.key !== "ArrowRight" &&
        e.key !== "Home" &&
        e.key !== "End"
      ) {
        return;
      }
      e.preventDefault();
      var btns = Array.prototype.slice.call(
        nav.querySelectorAll("button.customled-pdp-tabs__tab")
      );
      var current = nav.querySelector(
        "button.customled-pdp-tabs__tab.is-active"
      );
      var idx = btns.indexOf(current);
      var nextIdx;
      switch (e.key) {
        case "ArrowLeft":
          nextIdx = (idx - 1 + btns.length) % btns.length;
          break;
        case "ArrowRight":
          nextIdx = (idx + 1) % btns.length;
          break;
        case "Home":
          nextIdx = 0;
          break;
        case "End":
          nextIdx = btns.length - 1;
          break;
      }
      activateTab(nav, content, btns[nextIdx]);
      btns[nextIdx].focus();
    });
  }

  function activateTab(nav, content, btn) {
    var key = btn.dataset.tabKey;
    nav
      .querySelectorAll("button.customled-pdp-tabs__tab")
      .forEach(function (b) {
        var on = b === btn;
        b.classList.toggle("is-active", on);
        b.setAttribute("aria-selected", on ? "true" : "false");
        b.setAttribute("tabindex", on ? "0" : "-1");
      });
    content
      .querySelectorAll(".customled-pdp-tabs__panel")
      .forEach(function (p) {
        var on = p.dataset.tabKey === key;
        p.classList.toggle("is-active", on);
        p.hidden = !on;
      });
    hydrateEmbeds(content.querySelector(".customled-pdp-tabs__panel.is-active"));
  }

  // ---- Mobile accordion build ------------------------------------

  function buildAccordion() {
    var wrap = document.createElement("div");
    wrap.className =
      "customled-pdp-tabs customled-pdp-tabs--accordion";

    appendLead(wrap);

    state.tabSpecs.forEach(function (spec, i) {
      // Vendor's <collapsible-expandable> handles its own toggle
      // wiring (scripts.js:547 → CollapsibleExpandable extends
      // HTMLElement, listens for .facet-toggle clicks). Multi-open
      // by design — each instance toggles independently.
      //
      // Initial open state: first section expanded so the customer
      // sees content above the fold without an extra tap.
      var expandable = document.createElement("collapsible-expandable");
      expandable.setAttribute("expanded", i === 0 ? "true" : "false");

      var facet = document.createElement("div");
      facet.className = "facet";

      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "facet-toggle";
      btn.setAttribute("aria-expanded", i === 0 ? "true" : "false");

      // First span: heading label. The vendor expandable section
      // wraps an optional icon + heading inside this span, so the
      // facet-toggle > span:first-child CSS selector targets it.
      var labelSpan = document.createElement("span");
      labelSpan.textContent = spec.label;
      btn.appendChild(labelSpan);

      // Chevron — matches snippets/angle.liquid output verbatim:
      //   <span class="angle angle-down"><span class="angle-icon"></span></span>
      // Vendor CSS in component-facets.css rotates .angle-icon on
      // [aria-expanded="true"], giving the chevron rotation
      // animation for free.
      var angleWrap = document.createElement("span");
      angleWrap.className = "angle angle-down";
      var angleIcon = document.createElement("span");
      angleIcon.className = "angle-icon";
      angleWrap.appendChild(angleIcon);
      btn.appendChild(angleWrap);

      facet.appendChild(btn);

      var contentEl = document.createElement("div");
      contentEl.className = "facet-content rte";
      // Uniform slot-key attribute for reclaim across modes.
      contentEl.dataset.customledSlotKey = spec.key;
      moveNodesInto(spec.contentNodes, contentEl);
      facet.appendChild(contentEl);

      // PERF: hydrate the de-loaded YouTube iframe when its section opens. The
      // initially-expanded (i===0) section hydrates now; others on first toggle.
      if (i === 0) hydrateEmbeds(contentEl);
      btn.addEventListener("click", function () { hydrateEmbeds(contentEl); });

      expandable.appendChild(facet);
      wrap.appendChild(expandable);
    });

    return wrap;
  }

  // ---- Shared helpers --------------------------------------------

  function appendLead(wrap) {
    if (!state.leadHtml) return;
    var lead = document.createElement("div");
    lead.className = "customled-pdp-tabs__lead rte";
    lead.innerHTML = state.leadHtml;
    wrap.appendChild(lead);
  }

  function moveNodesInto(nodes, target) {
    // Move (not clone) so iframes, video, lazy-loaded images keep
    // their DOM identity. After this loop, `nodes` is a stale list
    // of references but each node is now living inside `target`.
    nodes.forEach(function (n) {
      target.appendChild(n);
    });
  }

  // PERF (M1.13): hydrate lazy embeds. The YouTube playlist iframe in the
  // product description has its src renamed to data-cl-src server-side
  // (sections/main-product.liquid) so it does NOT eager-load at parse (~0.94 MB).
  // We restore src -> load the video only when its tab/section is opened.
  function hydrateEmbeds(container) {
    if (!container) return;
    var frames = container.querySelectorAll("iframe[data-cl-src]");
    Array.prototype.forEach.call(frames, function (f) {
      f.setAttribute("src", f.getAttribute("data-cl-src"));
      f.removeAttribute("data-cl-src");
    });
  }

  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================================
 * CUSTOMLED PDP media-modal close-button pin (co-located here because this file
 * loads on the product template only; zero new requests per the 2026-07-16 perf
 * session). The vendor product image lightbox (product-modal.js) close button
 * .product-media-modal-toggle is position:fixed, so it drifts off-screen under
 * pinch-zoom. This re-pins it to the VISUAL viewport (counter-scaled) while the
 * modal's [open] attribute is set. It first neutralizes the button's own
 * top/right offset (transform is additive) then translates from top-left, and
 * restores everything on close. Additive; NO vendor edit. If a TFP upgrade
 * renames the button/attribute, this no-ops but nothing breaks. Mirrors the pin
 * in _customled-article-lightbox.js.
 * ========================================================================== */
(function () {
  'use strict';
  if (!window.visualViewport) return;
  var vv = window.visualViewport;
  var activeBtn = null;
  function pin(btn) {
    var s = 1 / vv.scale;
    var bw = btn.offsetWidth || 40;
    var m = 12 * s;
    var tx = vv.offsetLeft + vv.width - (bw * s) - m;
    var ty = vv.offsetTop + m;
    btn.style.transformOrigin = 'top left';
    btn.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + s + ')';
  }
  function onVV() { if (activeBtn) pin(activeBtn); }
  function start(btn) {
    if (activeBtn === btn) { pin(btn); return; }
    activeBtn = btn;
    btn.style.top = '0'; btn.style.left = '0'; btn.style.right = 'auto'; btn.style.bottom = 'auto';
    pin(btn);
    vv.addEventListener('resize', onVV);
    vv.addEventListener('scroll', onVV);
  }
  function stop() {
    if (!activeBtn) return;
    vv.removeEventListener('resize', onVV);
    vv.removeEventListener('scroll', onVV);
    activeBtn.style.transform = '';
    activeBtn.style.top = ''; activeBtn.style.left = ''; activeBtn.style.right = ''; activeBtn.style.bottom = '';
    activeBtn = null;
  }
  function watch(modal) {
    var btn = modal.querySelector('.product-media-modal-toggle');
    if (!btn) return;
    new MutationObserver(function () {
      if (modal.hasAttribute('open')) start(btn); else stop();
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });
    if (modal.hasAttribute('open')) start(btn);
  }
  function init() {
    document.querySelectorAll('product-modal, .product-media-modal').forEach(watch);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
